import { CdkDragEnter, CdkDropList, DragRef, moveItemInArray } from '@angular/cdk/drag-drop';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { FileService } from '../../../core/service/file.service';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';

@Component({
    selector: 'app-multi-file-upload-sortable',
    templateUrl: './multi-file-upload-sortable.component.html',
    styleUrls: ['./multi-file-upload-sortable.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiFileUploadSortableComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    public breakpointObserver: BreakpointObserver,
    protected fb: FormBuilder,
    protected fc: FormControlExFactory,
    private _fileService: FileService
  ) {}

  // Accept AbstractControl for template binding; used internally as a FormArray.
  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
    this.loadImagePreviews();
  }
  get forms(): FormArray {
    return this._forms;
  }
  @Input() maxFiles = 5;
  @Input() text = 'COMMON.ATTACH_FILE';
  @Input() allowedMimeTypes: string[] = [];
  @Input() isOnSidebar: boolean = false;
  @Input() readonly: boolean = false;
  @Output() fileAdded = new EventEmitter<File>();
  @Output() fileRemoved = new EventEmitter<number>();
  @Output() filesReordered = new EventEmitter<any>();

  @ViewChild(CdkDropList) placeholder: CdkDropList;

  private target: CdkDropList = null;
  private targetIndex: number;
  private source: CdkDropList = null;
  private sourceIndex: number;
  private dragRef: DragRef = null;

  protected enableDragDelay: boolean = true;

  @Input() externalClickEvent: Observable<void>;
  @Input() externalClearEvent: Observable<void>;

  private _sub = new Subscription();
  private _previewSub = new Subscription();
  ngOnInit(): void {
    this._sub.add(
      this.breakpointObserver.observe(['(min-width: 575px)']).subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.enableDragDelay = false;
        } else {
          this.enableDragDelay = true;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    this._previewSub.unsubscribe();
  }

  private loadImagePreviews(): void {
    this._previewSub.unsubscribe();
    this._previewSub = new Subscription();

    if (!this.forms) {
      return;
    }

    for (let form of this.forms.controls.map(f => f as FormGroup)) {
      const srcControl = form.controls['src'];
      const src = srcControl?.value;

      if (!src || typeof src !== 'string' || src.startsWith('data:')) {
        continue;
      }

      if (this._fileService.isWebImage(form.controls['mimeType'].value)) {
        this._previewSub.add(
          this._fileService.loadFileBlob(src).subscribe(file => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = _event => {
              srcControl.setValue(reader.result);
            };
          })
        );
      }
    }
  }

  ngAfterViewInit() {
    // hide placeholder
    const placeholderElement = this.placeholder.element.nativeElement;
    placeholderElement.parentNode.removeChild(placeholderElement);
  }

  onDropListDropped() {
    if (this.readonly) {
      return;
    }

    // ignore if dropped outside-ish
    if (!this.target) {
      return;
    }

    const placeholderElement: HTMLElement = this.placeholder.element.nativeElement;
    const placeholderParentElement: HTMLElement = placeholderElement.parentElement;

    // remove placeholder and add back the dragged item
    placeholderParentElement.removeChild(placeholderElement);
    placeholderParentElement.insertBefore(this.source.element.nativeElement, placeholderParentElement.children[this.sourceIndex]);

    // stop drag interaction with placeholder
    if (this.placeholder._dropListRef.isDragging()) {
      this.placeholder._dropListRef.exit(this.dragRef);
    }

    this.target = null;
    this.source = null;
    this.dragRef = null;

    // actually move the dragged item in the array
    if (this.sourceIndex !== this.targetIndex) {
      moveItemInArray(this.forms.controls, this.sourceIndex, this.targetIndex);
      this.filesReordered.emit(this.forms.controls.map(c => c.value));
    }
  }

  onDropListEntered({ item, container }: CdkDragEnter) {
    if (this.readonly) {
      return;
    }

    // ignore movement if over placeholder
    if (container == this.placeholder) {
      return;
    }

    const placeholderElement: HTMLElement = this.placeholder.element.nativeElement;
    const sourceElement: HTMLElement = item.dropContainer.element.nativeElement;
    const dropElement: HTMLElement = container.element.nativeElement;

    // set index based on position of item to drag on first interaction, after that base it of the placeholder
    const dragIndex: number = Array.prototype.indexOf.call(
      dropElement.parentElement.children,
      this.source ? placeholderElement : sourceElement
    );
    const dropIndex: number = Array.prototype.indexOf.call(dropElement.parentElement.children, dropElement);

    // on first interaction set source, and give the placeholder some size, remove the dragged item and add in the placeholder
    if (!this.source) {
      this.sourceIndex = dragIndex;
      this.source = item.dropContainer;

      sourceElement.parentElement.removeChild(sourceElement);
      dropElement.parentElement.appendChild(placeholderElement);
    }

    this.targetIndex = dropIndex;
    this.target = container;
    this.dragRef = item._dragRef;

    // place the placeholder where we are dragging
    dropElement.parentElement.insertBefore(placeholderElement, dropIndex > dragIndex ? dropElement.nextSibling : dropElement);

    // move the dragged item into the placeholder
    this.placeholder._dropListRef.enter(item._dragRef, item.element.nativeElement.offsetLeft, item.element.nativeElement.offsetTop);
  }
  // from example end

  get fileCount() {
    return this.forms?.controls?.length ?? 0;
  }

  clearAllFiles() {
    if (this.readonly) {
      return;
    }

    (this.forms as FormArray).controls = [];
  }

  getClass(mimeType: string): string {
    if (this._fileService.isPdf(mimeType)) {
      return 't-pdf';
    }
    if (this._fileService.isSpreadsheet(mimeType)) {
      return 't-xls';
    }
    if (this._fileService.isWordProcessor(mimeType)) {
      return 't-doc';
    }
    if (!this._fileService.isWebImage(mimeType)) {
      return 't-other';
    }
    return 't-image';
  }

  showImage(mimeType: string) {
    return this._fileService.isWebImage(mimeType);
  }

  delete(form: FormGroup) {
    if (this.readonly) {
      return;
    }

    let index = this.forms.controls.indexOf(form);
    this.forms.controls.splice(index, 1);
    this.forms.markAsDirty();
    this.fileRemoved.emit(index);
  }

  private fileIsValid(file: File): boolean {
    if (this._fileService.validateDocument(file) !== null) {
      return false;
    }
    if (this.allowedMimeTypes?.length > 0) {
      return this.allowedMimeTypes.includes(file.type);
    }
    return true;
  }

  cancel(e: Event) {
    e.stopPropagation();
  }

  onFileChange(files: FileList) {
    if (this.readonly) {
      return;
    }

    if (files && files.length > 0) {
      for (let index = 0; index < files.length; index++) {
        if (this.fileCount >= this.maxFiles) {
          return;
        }

        let file = files[index];

        if (this.fileIsValid(file)) {
          this.forms.markAsDirty();
          this.fileAdded.emit(file);
        }
      }
    }
  }
}
